"use client";

import { useState } from "react";
import GlossaryCard from "./glossary-card";
import GlossarySearch from "./glossary-search";
import AlphabetNav from "./alphabet-nav";
import Title from "@/components/title";

interface GlossaryTerm {
  id: string;
  term: string;
  category: string;
  definition: string;
  relatedTerms: string[];
}

interface GlossaryBrowserProps {
  terms: GlossaryTerm[];
}

export default function GlossaryBrowser({ terms }: GlossaryBrowserProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  // Filter terms based on search query, category, and active letter
  const filteredTerms = terms.filter((term) => {
    const matchesSearch = searchQuery
      ? term.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
        term.definition.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    const matchesCategory =
      selectedCategory && selectedCategory !== "All Categories"
        ? term.category === selectedCategory
        : true;

    const matchesLetter = activeLetter
      ? term.term[0].toUpperCase() === activeLetter
      : true;

    return matchesSearch && matchesCategory && matchesLetter;
  });

  // Group terms by first letter
  const groupedTerms = filteredTerms.reduce(
    (acc, term) => {
      const firstLetter = term.term[0].toUpperCase();
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter].push(term);
      return acc;
    },
    {} as Record<string, GlossaryTerm[]>
  );

  const sortedLetters = Object.keys(groupedTerms).sort();

  return (
    <>
      {/* Search and filters */}
      <GlossarySearch
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />

      {/* Alphabet navigation */}
      <AlphabetNav
        activeLetter={activeLetter}
        onLetterClick={(letter) => {
          setActiveLetter(activeLetter === letter ? null : letter);
          // Scroll to letter section
          if (activeLetter !== letter) {
            const element = document.getElementById(
              `letter-${letter.toLowerCase()}`
            );
            if (element) {
              const offset = 100;
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition =
                elementPosition + window.pageYOffset - offset;
              window.scrollTo({
                top: offsetPosition,
                behavior: "smooth",
              });
            }
          }
        }}
        onClearFilter={() => setActiveLetter(null)}
      />

      {/* Results */}
      {filteredTerms.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-muted text-lg">
            No terms found. Try adjusting your search or filters.
          </p>
          <button
            onClick={() => {
              setSearchQuery("");
              setSelectedCategory("");
              setActiveLetter(null);
            }}
            className="mt-4 text-theme-light hover:underline"
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <div className="space-y-12">
          {sortedLetters.map((letter) => (
            <section key={letter} id={`letter-${letter.toLowerCase()}`}>
              <div className="top-32 z-10 backdrop-blur-sm py-3 mb-6 border-b border-theme-light">
                <Title
                  level={2}
                  size="3xl"
                  weight="bold"
                  className="text-theme-light"
                >
                  {letter}
                </Title>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupedTerms[letter]
                  .sort((a, b) => a.term.localeCompare(b.term))
                  .map((term) => (
                    <GlossaryCard key={term.id} term={term} />
                  ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}
